import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import ViewToggle, { useViewMode } from "@/components/ViewToggle";
import { Plus, CalendarBlank, CheckCircle, Warning, XCircle } from "@phosphor-icons/react";

const STATUS_MAP = {
    pending: { cls: "bg-[var(--ink-2)] text-white border-[var(--ink-2)]", Icon: CalendarBlank, label: "Analizando…" },
    safe: { cls: "bg-[var(--sage)] text-white border-[var(--sage)]", Icon: CheckCircle, label: "Seguro" },
    caution: { cls: "bg-[var(--amber)] text-white border-[var(--amber)]", Icon: Warning, label: "Precaución" },
    unsafe: { cls: "bg-[var(--terra-2)] text-white border-[var(--terra-2)]", Icon: XCircle, label: "Desaconsejado" },
};

export default function Schedules() {
    const [items, setItems] = useState([]);
    const [view, setView] = useViewMode("schedules", "list");
    useEffect(() => { api.get("/schedules").then((r) => setItems(r.data)); }, []);

    return (
        <div className="fade-up">
            <PageHeader
                eyebrow={`Cronogramas · ${items.length}`}
                title="Cronogramas de suplementación"
                lead="Combina varias fórmulas en un mismo paciente y valida la seguridad de la combinación: dosis acumuladas, interacciones y timing."
                actions={
                    <Link to="/cronogramas/nuevo" className="btn-primary inline-flex items-center gap-2" data-testid="schedules-new-button">
                        <Plus size={14} weight="bold"/> Nuevo cronograma
                    </Link>
                }
            />
            <div className="px-8 md:px-12 py-8 max-w-6xl">
                <div className="flex items-center justify-end mb-4">
                    <ViewToggle value={view} onChange={setView} testid="schedules-view-toggle"/>
                </div>
                {items.length === 0 ? (
                    <div className="border hairline bg-white p-12 text-center text-[var(--ink-3)]" data-testid="schedules-empty">
                        Sin cronogramas todavía. <Link to="/cronogramas/nuevo" className="text-[var(--sage)] underline">Crear el primero →</Link>
                    </div>
                ) : view === "list" ? (
                    <div className="surface overflow-x-auto" data-testid="schedules-list">
                        <table className="data-table w-full">
                            <thead><tr><th>Cronograma</th><th>Paciente</th><th>Fórmulas</th><th>Estado</th><th>Fecha</th></tr></thead>
                            <tbody>
                                {items.map((s) => {
                                    const st = STATUS_MAP[s.safety?.status] || STATUS_MAP.caution;
                                    const I = st.Icon;
                                    return (
                                        <tr key={s.id} data-testid={`schedule-row-${s.id}`}>
                                            <td><Link to={`/cronogramas/${s.id}`} className="font-medium hover:text-[var(--sage)]">{s.name}</Link></td>
                                            <td className="text-[var(--ink-2)]">{s.patient_snapshot?.name || "—"}</td>
                                            <td className="mono text-xs">{(s.items || []).length}</td>
                                            <td><span className={`citation-badge ${st.cls}`}><I size={12}/> {st.label}</span></td>
                                            <td className="text-xs text-[var(--ink-3)]">{new Date(s.created_at).toLocaleDateString("es-ES")}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="grid md:grid-cols-2 gap-px bg-[var(--line)] border hairline" data-testid="schedules-cards">
                        {items.map((s) => {
                            const st = STATUS_MAP[s.safety?.status] || STATUS_MAP.caution;
                            const I = st.Icon;
                            return (
                                <Link key={s.id} to={`/cronogramas/${s.id}`} className="bg-white p-6 hover:bg-[var(--cream)]" data-testid={`schedule-card-${s.id}`}>
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="label-eyebrow flex items-center gap-2"><CalendarBlank size={14}/> Cronograma</span>
                                        <span className={`citation-badge ${st.cls}`}><I size={12}/> {st.label}</span>
                                    </div>
                                    <h3 className="font-display text-xl tracking-tight leading-snug">{s.name}</h3>
                                    {s.patient_snapshot?.name && <div className="text-xs text-[var(--ink-2)] mt-1">Paciente: {s.patient_snapshot.name}</div>}
                                    <div className="text-xs mono text-[var(--ink-3)] mt-3">{(s.items || []).length} fórmulas · {new Date(s.created_at).toLocaleDateString("es-ES")}</div>
                                </Link>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}
